import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { ClientUser, UserInvite, UserSearchConfig } from '../models/client-user-management';
import { Observable, of } from 'rxjs';
import { UserToPersonMapModel, UserEmployerModel, UserTimeclockMapping } from '../models/person';
import { Client } from '../models/client';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject('env') private env: any,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any
  ) {}

  public getClientUsers(clientId: string): Observable<ClientUser[]> {
    return this.http.get<ClientUser[]>(`${this.env.apiUrlRoot}/Companies/${clientId}/Users`);
  }

  public inviteUser(clientId: string, invite: UserInvite) {
    const headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
    return this.http.post(`${this.env.apiUrlRoot}/Companies/${clientId}/Users`, invite, {
      headers
    });
  }
  public resendInvite(clientId: string, userId: string, email: string) {
    const body = {
      EmailAddress: email
    };
    return this.http.post(
      `${this.env.apiUrlRoot}/Companies/${clientId}/Users/${userId}/ResendInvite`,
      body
    );
  }
  public addAclJoin(clientId: string, userId: string, role: string[]) {
    const body = role.map(x => {
      return { Role: x };
    });

    return this.http.post(`${this.env.apiUrlRoot}/Companies/${clientId}/Users/${userId}/acl`, body);
  }
  public removeAclJoin(clientId: string, userId: string, role: string[]) {
    const body = role.map(x => {
      return { Role: x };
    });
    return this.http.delete(`${this.env.apiUrlRoot}/Companies/${clientId}/Users/${userId}/acl`, {
      body
    });
  }
  public searchUsers(config: UserSearchConfig) {
    let params = new HttpParams({
      fromObject: { ...config }
    });
    return this.http.get<ClientUser[]>(`${this.env.apiUrlRoot}/User/Search`, { params: params });
  }
  public userHasPhoneNumber(id: string) {
    return this.http.get<{ Result: boolean }>(`${this.env.apiUrlRoot}/User/${id}/HasPhoneNumber`);
  }

  public unlockUser(userId: string, clientId: string) {
    return this.http.post(
      `${this.env.apiUrlRoot}/Companies/${clientId}/Users/${userId}/unlock`,
      {}
    );
  }

  public hasMultipleEmployers(userId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.env.apiUrlRoot}/User/${userId}/HasMultipleEmployers`);
  }

  public getUserTimekeepingConfig(userId: string, clientCode: string) {
    if (userId == null || clientCode == null) {
      return of(null);
    }
    return this.http.get<UserTimeclockMapping>(
      `${this.env.apiUrlRoot}/User/${userId}/TimekeepingSSO/${clientCode}`
    );
  }

  public addUserTimekeepingConfig(
    userId: string,
    clientCode: string,
    serviceId: number,
    timekeepingId: string
  ) {
    return this.http.post(`${this.env.apiUrlRoot}/User/${userId}/TimekeepingSSO/${clientCode}`, {
      TimekeepingId: timekeepingId,
      ServiceId: serviceId
    });
  }

  public deleteUserTimekeepingConfig(userId: string, clientCode: string) {
    return this.http.delete(`${this.env.apiUrlRoot}/User/${userId}/TimekeepingSSO/${clientCode}`);
  }

  public getUserEmployers(userId: string): Observable<UserEmployerModel[]> {
    return this.http.get<UserEmployerModel[]>(
      `${this.env.apiUrlRoot}/User/${userId}/UserEmployers`
    );
  }

  public getUserToPersonMappings(userId: string): Observable<UserToPersonMapModel[]> {
    return this.http.get<UserToPersonMapModel[]>(
      `${this.env.apiUrlRoot}/User/${userId}/UserPersonMap`
    );
  }

  public verifyUserToPersonMap(
    userId: string,
    clientCode: string,
    personId: number
  ): Observable<boolean> {
    let params = new HttpParams();
    params = params.append('clientCode', clientCode);
    params = params.append('personId', personId);
    return this.http.get<boolean>(`${this.env.apiUrlRoot}/User/${userId}/VerifyUserToPersonMap`, {
      params: params
    });
  }

  public getUserClients() {
    return this.http.get<Client[]>(`${this.env.apiUrlRoot}/User/Clients`);
  }
}
